/**
 * いま動いている場所に合った Platform を選ぶ。
 */

import { MockPlatform } from './MockPlatform';
import { NativePlatform } from './NativePlatform';
import { WebPlatform, type Platform } from './Platform';
import { nativeOS } from './native';
import { withTracking } from './trackedPlatform';

const MOCK_FLAG_KEY = 'meshi-merge:platform';

/** モックを許す場所。本番の Web 版では `?platform=mock` を付けても効かない。 */
export function mockAllowedOn(hostname: string, isDev: boolean): boolean {
  if (isDev) return true;
  if (hostname === 'localhost' || hostname.startsWith('127.') || hostname === '[::1]') return true;
  // Cloudflare Pages のプレビュー（staging.<project>.pages.dev / <hash>.<project>.pages.dev）
  return hostname.startsWith('staging.');
}

let cached: Platform | null = null;

export function detectPlatform(): Platform {
  if (cached) return cached;
  // 広告・購入の結果を遊び方の記録に残す（中身の動きは変えない。Web版は包まない）
  cached = withTracking(choosePlatform());
  return cached;
}

function choosePlatform(): Platform {
  const os = nativeOS();
  if (os) {
    // iOS は広告＋課金、Android は広告だけ（NativePlatform の中で分ける）
    return new NativePlatform(os);
  }

  if (mockAllowedOn(location.hostname, import.meta.env.DEV)) {
    const param = new URLSearchParams(location.search).get('platform');
    try {
      // 県えらび → 県の画面へ移るとクエリが消えるので、タブを閉じるまで覚えておく
      // mock-android：Android 版（広告だけ・課金なし）の見え方を試す
      if (param === 'mock' || param === 'mock-android') sessionStorage.setItem(MOCK_FLAG_KEY, param);
      if (param === 'web') sessionStorage.removeItem(MOCK_FLAG_KEY);
      const flag = sessionStorage.getItem(MOCK_FLAG_KEY);
      if (flag === 'mock' || flag === 'mock-android') {
        return new MockPlatform({ purchases: flag === 'mock', priceFailures: mockPriceFailures() });
      }
    } catch {
      if (param === 'mock' || param === 'mock-android') {
        return new MockPlatform({ purchases: param === 'mock' });
      }
    }
  }

  return new WebPlatform();
}

/** `?mockprice=late`：起動時は値段が取れず、ショップを開いて取り直すと出る（`late2` なら2回目で）。 */
function mockPriceFailures(): number {
  const value = new URLSearchParams(location.search).get('mockprice');
  if (value === 'late') return 1;
  if (value === 'late2') return 2;
  return 0;
}
